import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NoteOutlinedIcon from '@mui/icons-material/NoteOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { format, formatDistanceToNow } from 'date-fns';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { RootState } from '../../store/store';
import { useStartImpersonationMutation } from './api/impersonationApi';
import { type CreateTestSessionResponse, useGetAdminMemberQuery } from './api/membersApi';
import AdminSessionHistory from './components/AdminSessionHistory';
import CreateTestSessionDialog from './components/CreateTestSessionDialog';
import MemberLifecycleSection from './MemberLifecycleSection';
import { startAdminImpersonationSession } from './utils/adminImpersonation';
import { buildTestSessionCreationMessage } from './utils/testSessionCreation';

function extractApiErrorDescription(error: unknown): string {
  if (!error || typeof error !== 'object') {
    return '';
  }

  const data = 'data' in error ? (error as { data?: unknown }).data : undefined;
  if (!data || typeof data !== 'object') {
    return '';
  }

  const errorDescription = (data as { error_description?: unknown }).error_description;
  return typeof errorDescription === 'string' ? errorDescription : '';
}

export default function MemberDetailPage() {
  const { memberId } = useParams<{ memberId: string }>();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const currentToken = useSelector((state: RootState) => state.auth.token);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [startImpersonationApi, { isLoading: isImpersonating }] = useStartImpersonationMutation();
  const [testSessionDialogOpen, setTestSessionDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { data, isLoading, error } = useGetAdminMemberQuery(
    { memberId: memberId! },
    { skip: !memberId }
  );

  const member = data?.member;
  const artifacts = data?.artifacts ?? [];
  // Absent when the backend cannot create test sessions in this environment;
  // an empty list means the member has no current operational coach.
  const currentCoaches = data?.current_coaches;
  const testSessionUnavailableReason =
    currentCoaches === undefined
      ? 'Test sessions are unavailable in this environment.'
      : currentCoaches.length === 0
        ? 'This member has no current coach, so a test session cannot be created.'
        : '';

  useEffect(() => {
    if (!member || location.hash !== '#lifecycle') {
      return;
    }

    document.getElementById('lifecycle')?.scrollIntoView({ block: 'start' });
  }, [location.hash, member]);

  const handleImpersonate = async () => {
    if (!member || !currentToken || !currentUser) {
      setSnackbar({
        open: true,
        message: 'Current admin session is unavailable.',
        severity: 'error',
      });
      return;
    }

    try {
      const result = await startImpersonationApi({ target_person_id: member.id }).unwrap();
      startAdminImpersonationSession({
        dispatch,
        navigate,
        currentToken,
        currentUser,
        result,
        returnPath: `${location.pathname}${location.search}`,
      });
    } catch (err) {
      const message =
        extractApiErrorDescription(err) ||
        (err as any)?.message ||
        'Failed to start impersonation.';
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  const handleTestSessionCreated = (result: CreateTestSessionResponse) => {
    setTestSessionDialogOpen(false);
    setSnackbar({
      open: true,
      message: buildTestSessionCreationMessage(result),
      severity: 'success',
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !member) {
    return (
      <Box sx={{ maxWidth: 1200, margin: '0 auto', padding: 3 }}>
        <Alert severity="error">
          Failed to load member details. {extractApiErrorDescription(error)}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/members')}
          sx={{ mt: 2 }}
        >
          Back to Members
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', padding: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/admin/members')}
        sx={{ mb: 2 }}
      >
        Back to Members
      </Button>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography variant="h5">{member.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {member.email}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Tooltip title="View as this member">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={isImpersonating ? <CircularProgress size={16} /> : <VisibilityIcon />}
                  onClick={handleImpersonate}
                  disabled={isImpersonating}
                >
                  Impersonate
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                testSessionUnavailableReason ||
                'Create a past coaching session with a pasted transcript for this member'
              }
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ScienceOutlinedIcon />}
                  onClick={() => setTestSessionDialogOpen(true)}
                  disabled={testSessionUnavailableReason !== ''}
                >
                  Create Test Session
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 3, mt: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            First login:{' '}
            {member.first_login_at
              ? format(new Date(member.first_login_at), 'MMM d, yyyy h:mm a')
              : 'Unknown'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last active:{' '}
            {member.last_active_at
              ? formatDistanceToNow(new Date(member.last_active_at), { addSuffix: true })
              : 'Unknown'}
          </Typography>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4">{data.session_stats.session_count}</Typography>
            <Typography variant="body2" color="text.secondary">
              Sessions
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4">{artifacts.length}</Typography>
            <Typography variant="body2" color="text.secondary">
              Artifacts
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <MemberLifecycleSection personId={member.id} />

      <Typography variant="h6" sx={{ mb: 2 }}>
        Sessions
      </Typography>
      <AdminSessionHistory memberId={member.id} />

      <Typography variant="h6" sx={{ mb: 2 }}>
        Artifacts
      </Typography>
      <Paper>
        {artifacts.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No artifacts found.</Typography>
          </Box>
        ) : (
          <List>
            {artifacts.map((artifact, index) => (
              <Box key={artifact.id}>
                {index > 0 && <Divider />}
                <ListItem>
                  <ListItemIcon>
                    <NoteOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={artifact.title}
                    secondary={
                      <Box component="span">
                        <Typography variant="caption" component="span" sx={{ display: 'block' }}>
                          Type: {artifact.profile_slug}
                        </Typography>
                        <Typography variant="caption" component="span" sx={{ display: 'block' }}>
                          Updated: {format(new Date(artifact.updated_at), 'MMM d, yyyy h:mm a')}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              </Box>
            ))}
          </List>
        )}
      </Paper>

      {testSessionDialogOpen && currentCoaches && currentCoaches.length > 0 && (
        <CreateTestSessionDialog
          open={testSessionDialogOpen}
          memberId={member.id}
          memberName={member.name}
          memberEmail={member.email}
          coaches={currentCoaches}
          onClose={() => setTestSessionDialogOpen(false)}
          onCreated={handleTestSessionCreated}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
